import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Input, Modal, Radio, Spin, Tag, Tooltip, message } from 'antd';
import {
  DeleteOutlined, PlusOutlined, ThunderboltOutlined, TranslationOutlined,
} from '@ant-design/icons';
import styled from 'styled-components';

import { copyFieldsApi } from '../../../api/presentationsApi';
import {
  ICopyDraftReport,
  ICopyEditorList,
  ICopyEditorState,
  ICopyTranslateReport,
} from '../../../types/models/presentations';

/**
 * Write the authored copy a brochure needs — every list, every item, one
 * language at a time.
 *
 * The trigger-as-children convention, same as `FloorPlanWizard` and
 * `MapImageModal`, so this drops into a row action, a menu item, or the
 * template drawer's pre-generation gate without being taken apart first.
 *
 * Two things shape the layout:
 *
 * - **One language: the template's.** A template is in one language, so that
 *   is the copy being prepared. A development's copy serves templates in every
 *   language, so there it is a switch.
 * - **A list is edited as a list.** A range list (one to four floor-plan items)
 *   gets Add and Remove; a fixed list (six feature bullets) does not, because
 *   its length is not a choice anybody is making. Showing four boxes and hoping
 *   somebody leaves two blank is how a brochure ends up with holes in it.
 *
 * **Draft with AI** runs the co-writer. What it writes arrives marked Draft and
 * stays a draft until somebody saves it, so a person's copy is never replaced
 * and the gate still says the drafts are unread.
 *
 * **Translate from ES** brings the copy over from another language and saves
 * it at once — the team writes in Spanish. It arrives marked Translated and
 * counts as reviewed (a translated draft stays a draft). What a person wrote in
 * this language is kept.
 *
 * See `COPY_FIELDS.md` in the gutenberg repo.
 */

/** Separates the parts of a pending-edit key. None of them contains it. */
const CELL = '::';

const Body = styled.div<{ embedded?: boolean }>`
  ${({ embedded }) => (embedded ? '' : `
  max-height: 62vh;
  overflow-y: auto;
  padding: 16px 8px;
  margin: -24px 0;
  `)}

  .list {
    padding: 4px 0 18px;
    border-bottom: 1px solid #f0f0f0;

    &:last-child { border-bottom: none; }
  }

  .list-head {
    display: flex;
    align-items: baseline;
    gap: 8px;
    margin-bottom: 2px;
  }

  .label { font-weight: 600; }

  /* The intent is the only guidance a writer gets, and for a never-AI list it
     is the only way to know where the answer comes from at all. */
  .intent {
    color: #8c8c8c;
    font-size: 12px;
    line-height: 1.5;
    margin-bottom: 10px;
  }

  .item {
    border-left: 2px solid #f0f0f0;
    padding: 8px 0 8px 12px;
    margin-bottom: 10px;

    &.dropped { opacity: 0.45; }
  }

  .item-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 11px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #8c8c8c;
    margin-bottom: 6px;
  }

  .langs {
    display: grid;
    grid-template-columns: repeat(var(--lang-count, 2), 1fr);
    gap: 12px;
  }

  .lang-title {
    font-size: 11px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #8c8c8c;
    margin-bottom: 4px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .part-name { font-size: 11px; color: #595959; margin: 6px 0 2px; }

  .count { font-variant-numeric: tabular-nums; }
  .count.over { color: #cf1322; }

  /* A list a person must write themselves must not look like one that merely
     happens to be empty. The risk is nobody noticing at a glance. */
  .list.needs-a-person .label::after {
    content: ' · a person has to write this';
    font-weight: 400;
    font-size: 11px;
    color: #d46b08;
  }

  .list.error .item { border-left-color: #ff4d4f; }

  .error-text { color: #cf1322; font-size: 12px; margin-top: 4px; }

  .cowriter {
    display: flex;
    gap: 8px;
    align-items: center;
    flex-wrap: wrap;
    margin-bottom: 12px;

    .hint { color: #8c8c8c; font-size: 12px; margin-right: auto; }
  }
`;

/** Tell the person what the co-writer did, and what it could not do. */
const summarise = (report: ICopyDraftReport, state: ICopyEditorState,
                   language: string) => {
  const labelOf = (root: string) =>
    state.lists.find((list) => list.root === root)?.label ?? root;
  const total = Object.values(report.drafted).reduce((sum, n) => sum + n, 0);
  const code = language.toUpperCase();

  if (total === 0) {
    message.info(`Nothing to draft in ${code} — every list is written, or needs a person.`);
  } else {
    message.success(`Drafted ${total} item${total === 1 ? '' : 's'} in ${code}. `
      + 'They are marked Draft until you read and save them.');
  }
  if (report.short.length) {
    message.warning(`${report.short.map(labelOf).join(', ')} came back short — `
      + 'the facts did not support more. Write the rest by hand.', 8);
  }
};

/** Tell the person what a translation did. */
const summariseTranslation = (report: ICopyTranslateReport, source: string,
                              target: string) => {
  const total = Object.values(report.translated).reduce((sum, n) => sum + n, 0);
  const kept = Object.values(report.kept).reduce((sum, n) => sum + n, 0);
  const from = source.toUpperCase();
  const into = target.toUpperCase();
  if (total === 0) {
    message.info(`Nothing to translate from ${from}.`);
    return;
  }
  message.success(`Translated ${total} item${total === 1 ? '' : 's'} from ${from} `
    + `into ${into}, and saved them.`
    + (kept ? ` ${kept} you wrote in ${into} ${kept === 1 ? 'was' : 'were'} kept.` : ''));
};

/** The editor's own footer: status on the left, the buttons on the right. */
const Footer = styled.div<{ embedded?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: flex-end;
  ${({ embedded }) => (embedded
    ? 'padding-top: 12px;'
    : 'margin: 24px -24px -24px; padding: 10px 16px; border-top: 1px solid #f0f0f0;')}

  .status { margin-right: auto; color: #8c8c8c; }
`;

export interface CopyFieldsEditorProps {
  listingId?: number;
  /** `Identifiable.id` is `number | string` across this codebase. */
  templateId?: number | string;
  /**
   * Edit a development's slide copy instead of a listing's. The server picks
   * the template the lists are laid out from.
   */
  developmentId?: number;
  /**
   * Only these lists, in this order — one slide's, in the Prepare screen.
   * Drafting is limited to them too. Omit for every list the template draws.
   */
  roots?: string[];
  /**
   * The language to edit. Defaults to the template's; a development's copy,
   * which has none, gets a switch.
   */
  language?: string;
  /** Fired after a successful save, with whether anything is still blocking. */
  onSaved?: (stillBlocking: boolean) => void;
  /** Whether there are edits nobody has saved, so a parent can ask first. */
  onDirtyChange?: (dirty: boolean) => void;
  /**
   * In a modal: offered as Close, and called once a save leaves nothing
   * blocking. Embedded, there is nothing to close.
   */
  onClose?: () => void;
  /** Laid out inline, scrolling with the page, rather than inside a modal. */
  embedded?: boolean;
}

export interface CopyFieldsModalProps
  extends Omit<CopyFieldsEditorProps, 'onClose' | 'embedded' | 'onDirtyChange'> {
  /** Shown in the header, so somebody with several open knows which is which. */
  listingName?: string;
  /** Controlled mode — omit both and the modal owns its own open state. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children?: ReactNode;
}

const partNames = (list: ICopyEditorList) =>
  (list.parts.length ? list.parts.map((part) => part.name) : ['']);

/**
 * The token an item's part occupies — the key values are actually stored under.
 * A scalar's one item is stored under the root itself.
 */
export const tokenFor = (root: string, index: number, part: string, scalar = false) =>
  (scalar ? root : part ? `${root}.${index}.${part}` : `${root}.${index}`);

/** Whether an item has anything in it, in the language given. */
export const itemWritten = (
  list: ICopyEditorList, index: number, language: string,
  override?: (token: string) => string | undefined
) => partNames(list).some((part) => {
  const token = tokenFor(list.root, index, part, list.scalar);
  const pending = override?.(token);
  const text = pending !== undefined
    ? pending
    : (list.items[index]?.values?.[language]?.[token] ?? '');
  return text.trim().length > 0;
});

/**
 * How many items short of usable this list is, in this language.
 *
 * Mirrors `copy_fields.missing_required` on the server, which remains the
 * authority — this exists so the modal can mark itself without a round trip.
 */
export const shortfall = (
  list: ICopyEditorList, language: string,
  override?: (token: string) => string | undefined
) => {
  if (!list.declared) return list.min_items;
  const written = list.items.filter(
    (_, index) => itemWritten(list, index, language, override)).length;
  return Math.max(0, list.min_items - written);
};

/**
 * The lists themselves, with their own Save. Loads when mounted, so a modal
 * mounts it on open and the Prepare screen mounts it per slide.
 */
export const CopyFieldsEditor = ({
  listingId, templateId, developmentId, roots, language: languageProp, onSaved,
  onDirtyChange, onClose, embedded,
}: CopyFieldsEditorProps) => {
  const [state, setState] = useState<ICopyEditorState>();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  /** Edits not yet saved, keyed `token::language`. */
  const [edits, setEdits] = useState<Record<string, string>>({});
  /**
   * Errors appear after a save attempt, not while typing. Every field starts
   * empty; a form that opens red teaches people to ignore red.
   */
  const [showErrors, setShowErrors] = useState(false);
  /**
   * Range-list slots the person has opened up beyond what is already written.
   * Keyed by root. Adding an item is a UI act — nothing is stored until there
   * is text to store, so an Add that is never typed into simply goes away.
   */
  const [opened, setOpened] = useState<Record<string, number>>({});
  /** The language the co-writer is drafting, while it is. */
  const [drafting, setDrafting] = useState<string | null>(null);
  /** The language being translated from, while it is. */
  const [translating, setTranslating] = useState<string | null>(null);
  /** The switch's choice, where there is no template language to follow. */
  const [chosen, setChosen] = useState<string>();

  // The same editor for a listing's copy and a development's.
  const fetchState = useCallback(() => (developmentId
    ? copyFieldsApi.forDevelopment(developmentId)
    : copyFieldsApi.forListing(listingId as number, templateId as number)),
  [developmentId, listingId, templateId]);

  const load = useCallback(() => {
    setLoading(true);
    fetchState()
      .then((data) => {
        setState(data);
        setEdits({});
        setOpened({});
        setShowErrors(false);
      })
      .catch(() => message.error('Could not load the copy for this property.'))
      .finally(() => setLoading(false));
  }, [fetchState]);

  useEffect(() => { load(); }, [load]);

  const dirty = Object.keys(edits).length > 0;
  useEffect(() => { onDirtyChange?.(dirty); }, [dirty, onDirtyChange]);

  /** The lists shown: one slide's when `roots` is given, in its order. */
  const rootsKey = roots?.join('|');
  const lists = useMemo(() => {
    if (!state) return [];
    if (!roots) return state.lists;
    return roots
      .map((root) => state.lists.find((list) => list.root === root))
      .filter((list): list is ICopyEditorList => !!list);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, rootsKey]);
  /** The one language on screen. */
  const shownIn = (from?: ICopyEditorState) =>
    languageProp ?? from?.template_language ?? chosen ?? from?.languages[0] ?? '';
  const shown = shownIn(state);
  /** No language is given: a development's copy, so the person picks. */
  const switchable = !languageProp && !!state && !state.template_language;
  const blockingIn = (fresh: ICopyEditorState) => fresh.lists
    .filter((list) => !roots || roots.includes(list.root))
    .some((list) => shortfall(list, shownIn(fresh)) > 0 || !list.declared);

  const cellKey = (token: string, language: string) =>
    `${token}${CELL}${language}`;
  const pending = (token: string, language: string) =>
    edits[cellKey(token, language)];

  const languages = useMemo(() => (shown ? [shown] : []), [shown]);
  /** Other languages with copy on these lists — what can be translated from. */
  const sources = (state?.languages ?? []).filter((language) => language !== shown
    && lists.some((list) => list.items.some((_, index) => itemWritten(list, index, language))));

  const textOf = (list: ICopyEditorList, index: number, part: string,
                  language: string) => {
    const token = tokenFor(list.root, index, part, list.scalar);
    const edit = pending(token, language);
    return edit !== undefined
      ? edit
      : (list.items[index]?.values?.[language]?.[token] ?? '');
  };

  /**
   * How many item slots to show for a list.
   *
   * A fixed list shows all of them — the count is not a choice. A range list
   * shows what is written, plus anything opened by hand, but never fewer than
   * the minimum and never more than the deck has room for.
   */
  const visibleCount = (list: ICopyEditorList) => {
    if (list.kind !== 'range') return list.slots;
    const written = list.items.filter((_, index) => languages.some(
      (language) => itemWritten(list, index, language,
        (token) => pending(token, language)))).length;
    return Math.min(
      list.slots,
      Math.max(list.min_items, written, opened[list.root] ?? 0, 1));
  };

  const blocked = useMemo(() => (state
    ? lists.filter((list) => languages.some(
      (language) => shortfall(list, language,
        (token) => pending(token, language)) > 0 || !list.declared))
    : []), [state, lists, edits, languages]);

  const draftCount = useMemo(() => (state
    ? lists.reduce((total, list) => total + list.items.reduce(
      (sub, item) => sub + languages.filter((language) => Object.values(
        item.origins?.[language] ?? {}).includes('ai_draft')).length, 0), 0)
    : 0), [state, lists, languages]);

  const clearItem = (list: ICopyEditorList, index: number) => {
    const cleared: Record<string, string> = {};
    languages.forEach((language) => partNames(list).forEach((part) => {
      cleared[cellKey(tokenFor(list.root, index, part, list.scalar), language)] = '';
    }));
    setEdits((prev) => ({ ...prev, ...cleared }));
  };

  /** Any list the co-writer is allowed to touch. */
  const canDraft = lists.some((list) => list.declared && list.ai === 'draft');

  const draftIn = async (language: string) => {
    setDrafting(language);
    try {
      const { report, state: fresh } = developmentId
        ? await copyFieldsApi.draftDevelopment(developmentId, language)
        : await copyFieldsApi.draft(listingId as number, templateId as number, language,
          roots);
      setState(fresh);
      setOpened({});
      setShowErrors(false);
      onSaved?.(blockingIn(fresh));
      summarise(report, fresh, language);
    } catch (e: any) {
      message.error(e?.response?.data?.detail ?? 'The co-writer could not draft that.');
    } finally {
      setDrafting(null);
    }
  };

  const save = async () => {
    const values = Object.entries(edits).map(([composite, text]) => {
      const [key, language] = composite.split(CELL);
      return { key, language, text };
    });
    if (!values.length) { onClose?.(); return; }

    setSaving(true);
    try {
      if (developmentId) await copyFieldsApi.saveDevelopment(developmentId, values);
      else await copyFieldsApi.save(listingId as number, values);
      // Reload rather than patching state by hand: pressing save is what turns
      // a draft into approved copy, so the origins the server now holds are
      // exactly the thing that changed.
      const fresh = await fetchState();
      setState(fresh);
      setEdits({});
      const stillBlocking = blockingIn(fresh);
      onSaved?.(stillBlocking);
      message.success('Copy saved.');
      if (stillBlocking) setShowErrors(true);
      else onClose?.();
    } catch (e: any) {
      message.error(e?.response?.data?.detail ?? 'Could not save that.');
    } finally {
      setSaving(false);
    }
  };

  const translateFrom = async (source: string) => {
    setTranslating(source);
    try {
      const { report, state: fresh } = developmentId
        ? await copyFieldsApi.translateDevelopment(developmentId, source, shown)
        : await copyFieldsApi.translate(listingId as number, templateId as number, source,
          shown, roots);
      setState(fresh);
      setOpened({});
      setShowErrors(false);
      onSaved?.(blockingIn(fresh));
      summariseTranslation(report, source, shown);
    } catch (e: any) {
      message.error(e?.response?.data?.detail ?? 'Could not translate that.');
    } finally {
      setTranslating(null);
    }
  };

  const busy = !!drafting || !!translating;
  const reloadsTip = (what: string) => (dirty
    ? `Save or undo your changes first — ${what} reloads the copy.` : '');

  const toolbar = shown && (canDraft || sources.length > 0 || switchable) && (
    <div className="cowriter">
      {switchable && (
        <Radio.Group
          size="small"
          value={shown}
          disabled={busy}
          onChange={(e) => {
            // Only one language is on screen, so switching would hide unsaved
            // edits in this one.
            if (dirty) {
              message.warning('Save or undo your changes before switching language.');
              return;
            }
            setChosen(e.target.value);
          }}
        >
          {(state?.languages ?? []).map((language) => (
            <Radio.Button key={language} value={language}>
              {language.toUpperCase()}
            </Radio.Button>
          ))}
        </Radio.Group>
      )}
      <span className="hint">
        {canDraft && 'AI drafts stay drafts until you save them. '}
        Your own copy is never replaced.
      </span>
      {sources.map((source) => (
        <Tooltip key={source} title={reloadsTip('translating') || `Translates the ${
          source.toUpperCase()} copy into ${shown.toUpperCase()} and saves it.`}>
          <Button
            size="small"
            icon={<TranslationOutlined />}
            loading={translating === source}
            disabled={busy || dirty}
            onClick={() => translateFrom(source)}
          >
            Translate from {source.toUpperCase()}
          </Button>
        </Tooltip>
      ))}
      {canDraft && (
        <Tooltip title={reloadsTip('drafting')}>
          <Button
            size="small"
            icon={<ThunderboltOutlined />}
            loading={drafting === shown}
            disabled={busy || dirty}
            onClick={() => draftIn(shown)}
          >
            Draft with AI
          </Button>
        </Tooltip>
      )}
    </div>
  );

  const footer = (
    <Footer embedded={embedded}>
      <span className="status">
        {blocked.length > 0
          ? `${blocked.length} list${blocked.length === 1 ? '' : 's'} still needed`
          : draftCount > 0
            ? `${draftCount} unreviewed draft${draftCount === 1 ? '' : 's'}`
            : ''}
      </span>
      {onClose && <Button onClick={onClose}>Close</Button>}
      <Button
        type="primary"
        loading={saving}
        disabled={!dirty}
        onClick={save}
      >
        {embedded ? 'Save copy' : 'Save'}
      </Button>
    </Footer>
  );

  return (
    <div>
        {loading && <Spin />}

        {!loading && state && lists.length === 0 && (
          <Alert
            type="info"
            showIcon
            message={roots ? 'Nothing to write on this slide.' : 'This template has no copy fields.'}
            description="Everything on it comes from the listing's own data."
          />
        )}

        {!loading && showErrors && blocked.length > 0 && (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 12 }}
            message={`${blocked.length} list${
              blocked.length === 1 ? '' : 's'} still need copy`}
            description="Needed before this can be generated."
          />
        )}

        {!loading && state && lists.length > 0 && (
          <Body embedded={embedded} style={{ ['--lang-count' as any]: languages.length }}>
            {toolbar}
            {lists.map((list) => {
              const count = visibleCount(list);
              const missing = languages.some(
                (language) => shortfall(list, language,
                  (token) => pending(token, language)) > 0);
              const canAdd = list.kind === 'range' && count < list.slots;
              const canRemove = list.kind === 'range' && count > list.min_items;

              return (
                <div
                  key={list.root}
                  className={[
                    'list',
                    list.ai === 'never' ? 'needs-a-person' : '',
                    showErrors && missing ? 'error' : '',
                  ].filter(Boolean).join(' ')}
                >
                  <div className="list-head">
                    <span className="label">{list.label}</span>
                    {list.kind === 'range' ? (
                      <Tag>{list.min_items} to {list.slots}</Tag>
                    ) : (
                      <Tag>{list.slots} needed</Tag>
                    )}
                    {!list.declared && (
                      <Tooltip title={'The template draws this list but nobody '
                        + 'has described it. Describe it on the template first.'}>
                        <Tag color="error">Undescribed</Tag>
                      </Tooltip>
                    )}
                  </div>
                  {list.intent && <div className="intent">{list.intent}</div>}

                  {Array.from({ length: count }).map((_, index) => (
                    <div className="item" key={index}>
                      <div className="item-head">
                        <span>{list.scalar ? '' : `Item ${index + 1}`}</span>
                        {canRemove && index >= list.min_items && (
                          <Tooltip title={'Clears this item. The block is left '
                            + 'out of the brochure entirely, rather than '
                            + 'printed blank.'}>
                            <Button
                              type="text"
                              size="small"
                              icon={<DeleteOutlined />}
                              onClick={() => clearItem(list, index)}
                            />
                          </Tooltip>
                        )}
                      </div>

                      {partNames(list).map((part) => (
                        <div key={part || 'value'}>
                          {part && <div className="part-name">{part}</div>}
                          <div className="langs">
                            {languages.map((language) => {
                              const token = tokenFor(list.root, index, part, list.scalar);
                              const text = textOf(list, index, part, language);
                              const origin = list.items[index]
                                ?.origins?.[language]?.[token];
                              const untouched = pending(token, language) === undefined;
                              const max = list.parts.find(
                                (p) => p.name === part)?.max_chars ?? null;
                              const over = max != null && text.length > max;
                              return (
                                <div key={language}>
                                  <div className="lang-title">
                                    <span>
                                      {language.toUpperCase()}
                                      {origin === 'ai_draft' && untouched && (
                                        <Tag color="blue" style={{ marginLeft: 6 }}>
                                          Draft
                                        </Tag>
                                      )}
                                      {origin === 'translated' && untouched && (
                                        <Tooltip title={'Translated from another language. '
                                          + 'Worth a read; it counts as reviewed.'}>
                                          <Tag color="cyan" style={{ marginLeft: 6 }}>
                                            Translated
                                          </Tag>
                                        </Tooltip>
                                      )}
                                    </span>
                                    {max != null && (
                                      <Tooltip title={'The shape this sits in is '
                                        + 'small. Longer copy overflows, and '
                                        + 'nothing downstream warns you.'}>
                                        <span className={`count${over ? ' over' : ''}`}>
                                          {text.length}/{max}
                                        </span>
                                      </Tooltip>
                                    )}
                                  </div>
                                  <Input.TextArea
                                    rows={part === 'body' ? 3 : 1}
                                    value={text}
                                    placeholder={list.ai === 'never'
                                      ? 'Nobody can guess this — it has to be looked up'
                                      : ''}
                                    onChange={(e) => setEdits((prev) => ({
                                      ...prev,
                                      [cellKey(token, language)]: e.target.value,
                                    }))}
                                  />
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}

                  {canAdd && (
                    <Button
                      size="small"
                      icon={<PlusOutlined />}
                      onClick={() => setOpened((prev) => ({
                        ...prev, [list.root]: count + 1,
                      }))}
                    >
                      Add an item
                    </Button>
                  )}

                  {showErrors && missing && (
                    <div className="error-text">
                      At least {list.min_items} item
                      {list.min_items === 1 ? '' : 's'} needed.
                    </div>
                  )}
                </div>
              );
            })}
          </Body>
        )}
        {state && footer}
    </div>
  );
};

const CopyFieldsModal = ({
  listingName, open: openProp, onOpenChange, children, ...editor
}: CopyFieldsModalProps) => {
  const [openState, setOpenState] = useState(false);
  const controlled = openProp !== undefined;
  const open = controlled ? openProp : openState;
  const setOpen = (next: boolean) => {
    if (!controlled) setOpenState(next);
    onOpenChange?.(next);
  };

  return (
    <>
      {children && <span onClick={() => setOpen(true)}>{children}</span>}
      <Modal
        visible={open}
        onCancel={() => setOpen(false)}
        width={640}
        title={listingName ? `Copy — ${listingName}` : 'Brochure copy'}
        destroyOnClose
        footer={null}
      >
        {open && (
          <CopyFieldsEditor
            {...editor}
            onSaved={(stillBlocking) => editor.onSaved?.(stillBlocking)}
            onClose={() => setOpen(false)}
          />
        )}
      </Modal>
    </>
  );
};

export default CopyFieldsModal;
